import type { LensCalendar } from '~/types/leLens';

import {
  LENS_DEFAULT_CALENDAR,
  MONTHS,
  type WallClock,
  type YearInView,
  dayLabel,
  instantAt,
  spanLabel,
  wallClockOf,
} from './lensCalendar';

export const TIME_UNITS = ['second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'] as const;

export type TimeUnit = (typeof TIME_UNITS)[number];

export type TimeScale = { unit: TimeUnit; calendar: LensCalendar; yearInView: number };

export const SUB_DAY_UNITS: ReadonlyArray<TimeUnit> = ['second', 'minute', 'hour'];

const ELAPSED_MS: Partial<Record<TimeUnit, number>> = { second: 1000, minute: 60_000, hour: 3_600_000 };

const TYPICAL_MS: Record<TimeUnit, number> = {
  second: 1000,
  minute: 60_000,
  hour: 3_600_000,
  day: 86_400_000,
  week: 604_800_000,
  month: 2_629_746_000,
  quarter: 7_889_238_000,
  year: 31_556_952_000,
};

const SHORTEST_DAYS: Partial<Record<TimeUnit, number>> = { week: 7, month: 28, quarter: 89, year: 365 };

const TICK_STEPS: ReadonlyArray<[TimeUnit, number]> = [
  ['second', 1],
  ['second', 5],
  ['second', 15],
  ['second', 30],
  ['minute', 1],
  ['minute', 5],
  ['minute', 15],
  ['minute', 30],
  ['hour', 1],
  ['hour', 3],
  ['hour', 6],
  ['hour', 12],
  ['day', 1],
  ['day', 2],
  ['week', 1],
  ['week', 2],
  ['month', 1],
  ['quarter', 1],
  ['quarter', 2],
  ['year', 1],
  ['year', 2],
  ['year', 5],
  ['year', 10],
];

const CIVIL_DAY_RE = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/;
const CIVIL_TIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/;
const OFFSET_TIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/i;
const BOARD_ZONE_SUFFIX_RE = /_(?:sydt|aest|aedt|syd|local)$/i;
const GRAIN_RE = new RegExp(`^(${TIME_UNITS.join('|')})\\((.+)\\)$`);

export function parseGrain(name: string | undefined): { grain: TimeUnit; column: string } | undefined {
  const match = name?.trim().match(GRAIN_RE);
  return match ? { grain: match[1] as TimeUnit, column: match[2] } : undefined;
}

// An offset-free DATETIME is read in its column's zone by the server's rule: declared, else suffix, else UTC.
export function instantOf(
  value: unknown,
  calendar: LensCalendar = LENS_DEFAULT_CALENDAR,
  column = '',
  declaredZone?: string,
): number | undefined {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.getTime();
  }
  if (value && typeof value === 'object' && 'value' in value) {
    return instantOf((value as { value?: unknown }).value, calendar, column, declaredZone);
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  const text = value.trim();
  const day = CIVIL_DAY_RE.exec(text);
  if (day) {
    return validWall(
      { year: Number(day[1]), month: Number(day[2]), day: Number(day[3] ?? 1), hour: 0, minute: 0, second: 0 },
      calendar.zone,
    );
  }
  const civil = CIVIL_TIME_RE.exec(text);
  if (civil) {
    const [year, month, date, hour, minute, second = 0] = civil.slice(1).map((part) => Number(part ?? 0));
    return validWall({ year, month, day: date, hour, minute, second }, columnZone(column, calendar, declaredZone));
  }
  if (!OFFSET_TIME_RE.test(text)) {
    return undefined;
  }
  const parsed = Date.parse(text.replace(' ', 'T'));
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function truncate(instant: number, unit: TimeUnit, calendar: LensCalendar = LENS_DEFAULT_CALENDAR): number {
  const seconds = Math.floor(instant / 1000);
  if (unit === 'second') {
    return seconds * 1000;
  }
  const wall = wallClockOf(new Date(instant), calendar.zone);
  if (unit === 'minute') {
    return (seconds - wall.second) * 1000;
  }
  if (unit === 'hour') {
    return (seconds - wall.second - wall.minute * 60) * 1000;
  }
  const midnight = { ...wall, hour: 0, minute: 0, second: 0 };
  const start =
    unit === 'day'
      ? midnight
      : unit === 'week'
        ? shiftWallDays(midnight, -weekdayIndex(wall, calendar.weekStart))
        : unit === 'month'
          ? { ...midnight, day: 1 }
          : unit === 'quarter'
            ? { ...midnight, month: Math.floor((wall.month - 1) / 3) * 3 + 1, day: 1 }
            : { ...midnight, month: 1, day: 1 };
  return instantAt(start, calendar.zone).getTime();
}

export function shift(
  instant: number,
  unit: TimeUnit,
  amount: number,
  calendar: LensCalendar = LENS_DEFAULT_CALENDAR,
): number {
  const elapsed = ELAPSED_MS[unit];
  if (elapsed) {
    return instant + amount * elapsed;
  }
  const wall = wallClockOf(new Date(instant), calendar.zone);
  const moved =
    unit === 'day' || unit === 'week'
      ? shiftWallDays(wall, amount * (unit === 'week' ? 7 : 1))
      : shiftWallMonths(wall, amount * (unit === 'month' ? 1 : unit === 'quarter' ? 3 : 12));
  return instantAt(moved, calendar.zone).getTime();
}

export function unitOfSeries(
  instants: ReadonlyArray<number>,
  calendar: LensCalendar = LENS_DEFAULT_CALENDAR,
): TimeUnit {
  const sorted = [...new Set(instants)].sort((left, right) => left - right);
  const shortestGap = sorted.slice(1).reduce((gap, instant, index) => Math.min(gap, instant - sorted[index]), Infinity);
  const fits = (unit: TimeUnit) =>
    sorted.every((instant) => truncate(instant, unit, calendar) === instant) &&
    (!SHORTEST_DAYS[unit] ||
      (sorted.length > 1 && shortestGap >= SHORTEST_DAYS[unit]! * TYPICAL_MS.day - TYPICAL_MS.hour));
  return [...TIME_UNITS].reverse().find(fits) ?? 'second';
}

export function timeScaleOf(
  values: ReadonlyArray<unknown>,
  calendar: LensCalendar = LENS_DEFAULT_CALENDAR,
  column = '',
  grain?: TimeUnit,
): { scale: TimeScale; instants: Array<number> } | undefined {
  const instants = values.map((value) => instantOf(value, calendar, column));
  if (!instants.length || instants.some((instant) => instant === undefined)) {
    return undefined;
  }
  const known = instants as Array<number>;
  return {
    instants: known,
    scale: {
      unit: grain ?? unitOfSeries(known, calendar),
      calendar,
      yearInView: wallClockOf(new Date(Math.max(...known)), calendar.zone).year,
    },
  };
}

export function bucketText(instant: number, unit: TimeUnit, calendar: LensCalendar = LENS_DEFAULT_CALENDAR): string {
  const wall = wallClockOf(new Date(instant), calendar.zone);
  const day = civilText(wall);
  if (!SUB_DAY_UNITS.includes(unit)) {
    return day;
  }
  const offsetMinutes = Math.round((asUtc(wall) - Math.floor(instant / 1000) * 1000) / 60_000);
  const offset = `${offsetMinutes < 0 ? '-' : '+'}${pad(Math.floor(Math.abs(offsetMinutes) / 60))}:${pad(Math.abs(offsetMinutes) % 60)}`;
  return `${day}T${pad(wall.hour)}:${pad(wall.minute)}:${pad(wall.second)}${offset}`;
}

export function civilDayOfValue(value: unknown, calendar: LensCalendar = LENS_DEFAULT_CALENDAR): string | undefined {
  const instant = instantOf(value, calendar);
  return instant === undefined ? undefined : bucketText(instant, 'day', calendar);
}

export function intervalLabel(
  instant: number,
  unit: TimeUnit,
  options: { calendar?: LensCalendar; yearInView?: YearInView; withDay?: boolean } = {},
): string {
  const calendar = options.calendar ?? LENS_DEFAULT_CALENDAR;
  const yearInView = options.yearInView ?? wallClockOf(new Date(), calendar.zone).year;
  const wall = wallClockOf(new Date(instant), calendar.zone);
  const yearSuffix = yearInView === wall.year ? '' : ` ${wall.year}`;
  if (unit === 'year') {
    return String(wall.year);
  }
  if (unit === 'quarter') {
    return `Q${Math.floor((wall.month - 1) / 3) + 1}${yearSuffix}`;
  }
  if (unit === 'month') {
    return `${MONTHS[wall.month - 1]}${yearSuffix}`;
  }
  if (unit === 'week') {
    return spanLabel(civilText(wall), bucketText(shift(instant, 'day', 6, calendar), 'day', calendar), yearInView);
  }
  const day = dayLabel(civilText(wall), yearInView);
  if (unit === 'day') {
    return day;
  }
  const clock = clockLabel(wall, unit);
  return options.withDay ? `${day} ${clock}` : clock;
}

export function sequenceLabels(instants: ReadonlyArray<number>, scale: TimeScale): Array<string> {
  return instants.map((instant, index) => {
    if (!SUB_DAY_UNITS.includes(scale.unit)) {
      return intervalLabel(instant, scale.unit, scale);
    }
    if (truncate(instant, 'day', scale.calendar) === instant) {
      return intervalLabel(instant, 'day', scale);
    }
    const previous = instants[index - 1];
    const newDay =
      previous === undefined || truncate(previous, 'day', scale.calendar) !== truncate(instant, 'day', scale.calendar);
    return intervalLabel(instant, scale.unit, { ...scale, withDay: newDay });
  });
}

export function valueLabel(
  value: unknown,
  calendar: LensCalendar = LENS_DEFAULT_CALENDAR,
  column = '',
): string | undefined {
  const instant = instantOf(value, calendar, column);
  return instant === undefined
    ? undefined
    : intervalLabel(instant, parseGrain(column)?.grain ?? unitOfSeries([instant], calendar), {
        calendar,
        withDay: true,
      });
}

export function columnLabeller(
  values: ReadonlyArray<unknown>,
  calendar: LensCalendar = LENS_DEFAULT_CALENDAR,
  column = '',
): (value: unknown) => string | undefined {
  const present = values.filter((value) => value !== null && value !== undefined && value !== '');
  const timed = timeScaleOf(present, calendar, column, parseGrain(column)?.grain);
  return (value) => {
    const instant = timed ? instantOf(value, calendar, column) : undefined;
    return timed && instant !== undefined ? pointLabel(instant, timed.scale) : valueLabel(value, calendar, column);
  };
}

export function pointLabel(instant: number, scale: TimeScale): string {
  return intervalLabel(instant, scale.unit, { ...scale, withDay: true });
}

export function timeTicks(min: number, max: number, scale: TimeScale, maxCount: number): Array<number> {
  const finest = TIME_UNITS.indexOf(scale.unit);
  for (const [unit, step] of TICK_STEPS) {
    if (TIME_UNITS.indexOf(unit) < finest || (max - min) / (TYPICAL_MS[unit] * step) > maxCount + 1) {
      continue;
    }
    const ticks = stepTicks(min, max, unit, step, scale.calendar);
    if (ticks.length <= maxCount) {
      return ticks;
    }
  }
  return [min, max];
}

export function tickUnit(ticks: ReadonlyArray<number>, scale: TimeScale): TimeUnit {
  const unit = [...TIME_UNITS]
    .reverse()
    .find((candidate) => ticks.every((tick) => truncate(tick, candidate, scale.calendar) === tick));
  const coarsest = unit ?? scale.unit;
  const bounded = TIME_UNITS.indexOf(coarsest) < TIME_UNITS.indexOf(scale.unit) ? scale.unit : coarsest;
  return bounded === 'week' && scale.unit !== 'week'
    ? 'day'
    : bounded === 'quarter' && scale.unit !== 'quarter'
      ? 'month'
      : bounded;
}

export function isCurrentInterval(instant: number, unit: TimeUnit, calendar: LensCalendar, now = Date.now()): boolean {
  return truncate(now, unit, calendar) === instant;
}

export function partialPeriodNote(unit: TimeUnit): string {
  return unit === 'day' ? 'today, partial' : `this ${unit}, partial`;
}

function stepTicks(min: number, max: number, unit: TimeUnit, step: number, calendar: LensCalendar): Array<number> {
  const ticks: Array<number> = [];
  let at = truncate(min, unit, calendar);
  if (at < min) {
    at = shift(at, unit, 1, calendar);
  }
  for (let index = 0; at <= max; index += 1, at = shift(at, unit, 1, calendar)) {
    if (onStep(at, unit, step, index, calendar)) {
      ticks.push(at);
    }
  }
  return ticks;
}

function onStep(instant: number, unit: TimeUnit, step: number, index: number, calendar: LensCalendar): boolean {
  if (step === 1) {
    return true;
  }
  const wall = wallClockOf(new Date(instant), calendar.zone);
  const position =
    unit === 'second'
      ? wall.second
      : unit === 'minute'
        ? wall.minute
        : unit === 'hour'
          ? wall.hour
          : unit === 'quarter'
            ? Math.floor((wall.month - 1) / 3)
            : unit === 'year'
              ? wall.year
              : index;
  return position % step === 0;
}

function clockLabel(wall: WallClock, unit: TimeUnit): string {
  const twelve = wall.hour % 12 === 0 ? 12 : wall.hour % 12;
  const meridiem = wall.hour < 12 ? 'am' : 'pm';
  if (unit === 'second') {
    return `${twelve}:${pad(wall.minute)}:${pad(wall.second)} ${meridiem}`;
  }
  return unit === 'minute' || wall.minute ? `${twelve}:${pad(wall.minute)} ${meridiem}` : `${twelve} ${meridiem}`;
}

function columnZone(column: string, calendar: LensCalendar, declaredZone?: string): string {
  if (declaredZone) {
    return declaredZone;
  }
  const bare = parseGrain(column)?.column ?? column;
  return BOARD_ZONE_SUFFIX_RE.test(bare) ? calendar.zone : 'UTC';
}

function validWall(wall: WallClock, zone: string): number | undefined {
  const probe = new Date(Date.UTC(wall.year, wall.month - 1, wall.day));
  if (probe.getUTCMonth() !== wall.month - 1 || probe.getUTCDate() !== wall.day || wall.hour > 23 || wall.minute > 59) {
    return undefined;
  }
  return instantAt(wall, zone).getTime();
}

function weekdayIndex(wall: WallClock, weekStart: LensCalendar['weekStart']): number {
  const weekday = new Date(Date.UTC(wall.year, wall.month - 1, wall.day)).getUTCDay();
  return weekStart === 'monday' ? (weekday + 6) % 7 : weekday;
}

function shiftWallDays(wall: WallClock, days: number): WallClock {
  const moved = new Date(Date.UTC(wall.year, wall.month - 1, wall.day + days));
  return { ...wall, year: moved.getUTCFullYear(), month: moved.getUTCMonth() + 1, day: moved.getUTCDate() };
}

function shiftWallMonths(wall: WallClock, months: number): WallClock {
  const index = wall.month - 1 + months;
  const year = wall.year + Math.floor(index / 12);
  const month = (((index % 12) + 12) % 12) + 1;
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return { ...wall, year, month, day: Math.min(wall.day, lastDay) };
}

function asUtc(wall: WallClock): number {
  return Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
}

function civilText(wall: WallClock): string {
  return `${wall.year}-${pad(wall.month)}-${pad(wall.day)}`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
